/**
 * Google Ads conversion tag.
 *
 * Ads land on www.cleanlist.ai; the signup happens here. The marketing site's
 * gtag linker writes the click cookie (_gcl_aw) to the shared .cleanlist.ai
 * domain, so a conversion fired from this host is credited to the ad click
 * that paid for it. Nothing here reads a gclid; the cookie carries it.
 *
 * Everything is a no-op unless VITE_GOOGLE_ADS_ID is set, so a deploy without
 * the env vars changes nothing. Every call is wrapped: telemetry must never
 * take the app down (see best-effort-analytics.ts for the two ways it has).
 *
 * Env (all optional, all public):
 *   VITE_GOOGLE_ADS_ID            AW-XXXXXXXXX
 *   VITE_GOOGLE_ADS_LABEL_SIGNUP  label of the "Qualified Signup" action
 *   VITE_GOOGLE_ADS_LABEL_LIST    label of the "List Created" action
 *   VITE_GOOGLE_ADS_LABEL_PAID    label of the "Paid Subscription" action
 */
import { bestEffortAnalytics } from '@/lib/best-effort-analytics';
import { pageHasEmail, redactedGooglePage } from '@/lib/google-tag';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

type ConversionKind = 'signup' | 'list' | 'paid';

const ADS_ID: string | undefined = import.meta.env.VITE_GOOGLE_ADS_ID;
const LABELS: Record<ConversionKind, string | undefined> = {
  signup: import.meta.env.VITE_GOOGLE_ADS_LABEL_SIGNUP,
  list: import.meta.env.VITE_GOOGLE_ADS_LABEL_LIST,
  paid: import.meta.env.VITE_GOOGLE_ADS_LABEL_PAID,
};

/**
 * Consumer mailbox providers, matched on the first label of the domain so
 * "outlook.co.uk" and "yahoo.fr" count. Measured over 120 days: free webmail
 * converts signup -> paid at 0.31%, a company domain at 2.68%, and webmail is
 * 73% of all signups. Firing the signup conversion for everyone would train
 * the bidding algorithm to buy the 73% that never pays.
 */
const FREE_MAIL_LABELS = new Set([
  'gmail',
  'googlemail',
  'yahoo',
  'ymail',
  'rocketmail',
  'hotmail',
  'outlook',
  'live',
  'msn',
  'icloud',
  'me',
  'mac',
  'aol',
  'proton',
  'protonmail',
  'pm',
  'gmx',
  'yandex',
  'mail',
  'email',
  'zoho',
  'hey',
  'fastmail',
  'duck',
  'qq',
  '163',
  '126',
  'sina',
  'foxmail',
  'web',
  'freenet',
  'laposte',
  'orange',
  'wanadoo',
  'free',
  'sfr',
  'libero',
  'naver',
  'rediffmail',
  'inbox',
  'hushmail',
  'tutanota',
  'tuta',
  'example',
]);

export const isCompanyDomainEmail = (email?: string | null): boolean => {
  const domain = email?.split('@')[1]?.toLowerCase().trim();
  if (!domain || !domain.includes('.')) return false;
  return !FREE_MAIL_LABELS.has(domain.split('.')[0]);
};

const gtag = (...args: unknown[]): void => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
};

/** Load gtag.js once and configure the Ads destination. Call at app start. */
export const initGoogleAds = (): void => {
  // Same as initGoogleTag: gtag's `top=` would send the email in the URL.
  if (!ADS_ID || pageHasEmail()) return;
  bestEffortAnalytics(() => {
    // index.html already loads gtag.js for AW-18004391561 and calls
    // gtag('config') without the enhanced-conversions flag. A second config
    // call for the same id merges into the first, so attach the flag to the
    // existing tag rather than loading a second copy of the library.
    if (window.gtag) {
      window.gtag('config', ADS_ID, { allow_enhanced_conversions: true });
      return;
    }
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ADS_ID)}`;
    document.head.appendChild(script);
    window.gtag = gtag;
    gtag('js', new Date());
    gtag('set', redactedGooglePage());
    // Enhanced conversions: the signup event hashes the email client-side so
    // Google can match a conversion it lost to ITP or a cleared cookie.
    gtag('config', ADS_ID, { allow_enhanced_conversions: true });
  });
};

/**
 * Fire one conversion. `transactionId` is the dedupe key: Google drops a
 * repeat with the same id, so a re-rendered effect or a revisited success
 * page cannot double count.
 */
export const trackGoogleAdsConversion = (
  kind: ConversionKind,
  opts: { transactionId?: string; value?: number; email?: string | null } = {}
): void => {
  const label = LABELS[kind];
  if (!ADS_ID || !label || !window.gtag) return;
  bestEffortAnalytics(() => {
    if (opts.email) window.gtag?.('set', 'user_data', { email: opts.email });
    window.gtag?.('event', 'conversion', {
      send_to: `${ADS_ID}/${label}`,
      // The page now, not the one the tag loaded on, and without emails.
      ...redactedGooglePage(),
      ...(opts.value !== undefined
        ? { value: opts.value, currency: 'USD' }
        : {}),
      ...(opts.transactionId ? { transaction_id: opts.transactionId } : {}),
    });
  });
};

/**
 * Public list prices, used only as the conversion value on the success page.
 * The URL carries plan_name and credits, never the amount; the exact charge
 * reaches Google through the weekly offline upload from Stripe.
 */
export const planValueUsd = (planName?: string | null): number | undefined => {
  const n = planName?.toLowerCase() ?? '';
  if (n.includes('scale')) return 599;
  if (n.includes('pro')) return 229;
  if (n.includes('starter')) return 79;
  return undefined;
};
